/**
 * §14.3 — Object pooling. All pools allocate fully at scene load.
 * Zero allocation in the hot loop: acquire()/release() are O(1) and never call `new`.
 * Iteration is over the dense alive set in ascending pool_index (deterministic, A.4).
 */

export interface Poolable {
  pool_index: number;
  alive: boolean;
}

export class Pool<T extends Poolable> {
  readonly capacity: number;
  readonly name: string;
  /** All slots, index === pool_index. Never reordered. */
  readonly slots: T[];
  /** Free slot indices, LIFO. */
  private free: Int32Array;
  private freeTop: number;
  /** Dense ascending list of live pool indices, rebuilt lazily when dirty. */
  private aliveIdx: Int32Array;
  private aliveCount = 0;
  private dirty = true;
  highWater = 0;
  exhaustedCount = 0;

  constructor(name: string, capacity: number, factory: (index: number) => T) {
    this.name = name;
    this.capacity = capacity;
    this.slots = new Array(capacity);
    this.free = new Int32Array(capacity);
    this.aliveIdx = new Int32Array(capacity);
    for (let i = 0; i < capacity; i++) {
      const obj = factory(i);
      obj.pool_index = i;
      obj.alive = false;
      this.slots[i] = obj;
      // free list is LIFO; push in reverse so low indices are handed out first
      this.free[i] = capacity - 1 - i;
    }
    this.freeTop = capacity;
  }

  get count(): number {
    if (this.dirty) this.rebuild();
    return this.aliveCount;
  }

  get freeCount(): number {
    return this.freeTop;
  }

  /** Returns null when exhausted (deterministic denial, §14.3). */
  acquire(): T | null {
    if (this.freeTop === 0) {
      this.exhaustedCount++;
      return null;
    }
    const idx = this.free[--this.freeTop];
    const obj = this.slots[idx];
    obj.alive = true;
    this.dirty = true;
    const used = this.capacity - this.freeTop;
    if (used > this.highWater) this.highWater = used;
    return obj;
  }

  release(obj: T): void {
    if (!obj.alive) return;
    obj.alive = false;
    this.free[this.freeTop++] = obj.pool_index;
    this.dirty = true;
  }

  releaseAt(index: number): void {
    this.release(this.slots[index]);
  }

  private rebuild(): void {
    let n = 0;
    const slots = this.slots;
    const out = this.aliveIdx;
    for (let i = 0; i < this.capacity; i++) {
      if (slots[i].alive) out[n++] = i;
    }
    this.aliveCount = n;
    this.dirty = false;
  }

  /**
   * Dense ascending view of live slot indices. Valid until the next acquire/release.
   * Callers must read `count` from the returned length, not cache across mutations.
   */
  liveIndices(): Int32Array {
    if (this.dirty) this.rebuild();
    return this.aliveIdx.subarray(0, this.aliveCount);
  }

  /** Iterate live objects in ascending pool_index. Safe against release during iteration. */
  forEachLive(fn: (obj: T, index: number) => void): void {
    if (this.dirty) this.rebuild();
    const idx = this.aliveIdx;
    const n = this.aliveCount;
    for (let i = 0; i < n; i++) {
      const obj = this.slots[idx[i]];
      if (obj.alive) fn(obj, idx[i]);
    }
  }

  /** Oldest-first recycle for decorative pools (§14.3 particles). */
  releaseAllLive(): void {
    for (let i = 0; i < this.capacity; i++) {
      if (this.slots[i].alive) this.release(this.slots[i]);
    }
  }

  reset(): void {
    this.freeTop = this.capacity;
    for (let i = 0; i < this.capacity; i++) {
      this.slots[i].alive = false;
      this.free[i] = this.capacity - 1 - i;
    }
    this.aliveCount = 0;
    this.dirty = true;
    this.highWater = 0;
    this.exhaustedCount = 0;
  }

  /** A.6 — serialize the free list so pool_index assignment replays exactly. */
  serializeFreeList(): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.freeTop; i++) out.push(this.free[i]);
    return out;
  }

  restoreFreeList(list: number[]): void {
    this.freeTop = list.length;
    for (let i = 0; i < list.length; i++) this.free[i] = list[i];
    this.dirty = true;
  }
}
